import React from 'react';
import useAsyncHandler from '../../../../../../hooks/use-async-handler';
import {
  DOWNLOAD_RETRY_INTERVAL_MS,
  generateDownloadHandler,
  MAX_DOWNLOAD_RETRIES,
} from '../../../../../documents/item-render';
import { UploadStatusLabel } from '../../../../../documents/upload-status-label';
import {
  DocumentLink,
  ButtonStyledLink,
  Dialog,
} from '../../../../simple-styled-components';
import { Text } from '../../../../../base/tw-components';

import dayjs from 'dayjs';
import { FadeLoader } from 'react-spinners';

import { colors } from '../../../../../../style';
import { IOverviewDocument } from '../documents';

type Props = {
  item: IOverviewDocument;
  index: number;
};
export const LetterItem = ({ item, index }: Props) => {
  const [displayLoading, setDisplayLoading] = React.useState(false);
  const [downloadModal, setDownloadModal] = React.useState(false);
  const [modalLink, setModalLink] = React.useState('');

  const {
    data: s3Url,
    error: showError,
    setError: setShowError,
    isBusy: downloading,
    startRequest,
    reset,
  } = useAsyncHandler(
    generateDownloadHandler(item),
    undefined,
    undefined,
    (r) => r !== 'pending',
    '',
    MAX_DOWNLOAD_RETRIES,
    DOWNLOAD_RETRY_INTERVAL_MS,
  );

  const openDocument = React.useCallback(() => {
    setShowError(false);
    setDisplayLoading(true);
    startRequest();
  }, [setShowError, setDisplayLoading, startRequest]);

  const openDownloadModal = React.useCallback(
    (link: string) => {
      setModalLink(link);
      setDownloadModal(true);
    },
    [setDownloadModal],
  );

  const closeDownloadModal = () => {
    setDownloadModal(false);
    reset(); // Reset the s3Url state
  };

  React.useEffect(() => {
    if (s3Url.length > 0 && s3Url !== 'pending') {
      openDownloadModal(s3Url);
      setDisplayLoading(false);
    }
  }, [s3Url, openDownloadModal]);

  React.useEffect(() => {
    if (showError) {
      setDisplayLoading(false);
      setTimeout(() => {
        setShowError(false);
        reset();
      }, 7000);
    }
  }, [setShowError, setDisplayLoading, reset, showError]);

  return (
    <>
      {showError && (
        <Dialog
          id={`document-error-modal`}
          onClose={() => setShowError(false)}
          title={'Loading Error'}
        >
          {downloading && (
            <div className="tw-flex tw-justify-center tw-mt-6 tw-mb-0">
              <FadeLoader
                height={14}
                width={5}
                radius={2}
                margin={0}
                color={colors.blue}
              />
            </div>
          )}
          <Text fontSize="base" className=" tw-p-4 tw-pb-5">
            Sorry, there was a problem retrieving {item.name} from{' '}
            {dayjs(item.receivedDate).format('MM/DD/YY')}.
          </Text>
        </Dialog>
      )}

      {displayLoading && (
        <Dialog
          id={`document-loading-modal`}
          onClose={() => setDisplayLoading(false)}
          title={'Retrieving document'}
        >
          {downloading && (
            <div className="tw-flex tw-justify-center tw-mt-6 tw-mb-0">
              <FadeLoader
                height={14}
                width={5}
                radius={2}
                margin={0}
                color={colors.blue}
              />
            </div>
          )}
          <Text fontSize="base" align="center" className="tw-p-4 tw-pb-5">
            Retrieving {item.name} for you. This could take up to a minute.
          </Text>
        </Dialog>
      )}

      <div
        key={item.fileName}
        className={`tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 ${
          index % 2 === 0 ? 'tw-bg-unm-bg-light' : ''
        }`}
      >
        <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
          <div className="tw-min-w-0">
            {item.pending && (
              <Text
                id={`document-pending-name-${index}`}
                className="tw-truncate tw-font-semibold tw-text-unm-primary01-100"
              >
                {item.name}
              </Text>
            )}
            {!item.pending && (
              <div className="tw-font-semibold tw-text-base tw-leading-5 tw-text-unm-adhoc2 tw-line-clamp-1">
                {item.name}
              </div>
            )}
            <div className="tw-text-md tw-leading-5 tw-text-unm-adhoc2 tw-mt-1">
              {dayjs(item.receivedDate).format('MM/DD/YY')}
            </div>
          </div>
        </div>
        {!item.pending && (
          <DocumentLink
            id={`document-link-${index}`}
            onClick={openDocument}
            disabled={downloading}
            className="tw-shrink-0 tw-text-right tw-text-sm tw-font-semibold tw-text-unm-interactive01-400"
            style={{
              textDecoration: 'underline',
            }}
          >
            View
          </DocumentLink>
        )}
        {item.pending && (
          <UploadStatusLabel
            id={`document-upload-status-${index}`}
            status={item.uploadStatus}
          />
        )}
      </div>

      {downloadModal && (
        <Dialog
          id={`document-available-modal`}
          onClose={closeDownloadModal}
          title={'Document Available'}
        >
          <Text color={'unm-adhoc2'} className="tw-p-3 tw-pb-4">
            Your document has been securely retrieved and is now available for
            viewing.
          </Text>
          <ButtonStyledLink
            id={`open-document-link`}
            href={modalLink}
            target="_blank"
          >
            Open Document
          </ButtonStyledLink>
        </Dialog>
      )}
    </>
  );
};
